import ApiHttp from "byzantine/src/ApiHttp";
import {
  BankingTokenRecord,
  CreatedBankingTokenRecord,
  DeserializedBankingToken,
  PaginatedBankingTokenResponse,
  TokenSubmitData,
} from "./types";

export function createBankingToken(
  submitData: TokenSubmitData,
): Promise<CreatedBankingTokenRecord> {
  return ApiHttp.fetch(
    "banking/tokens/",
    { method: "POST" },
    { ...submitData },
  );
}

export function listBankingTokens(): Promise<PaginatedBankingTokenResponse> {
  return ApiHttp.fetch("banking/tokens/", { method: "GET" });
}

export function retrieveBankingToken(
  uuid: string,
): Promise<BankingTokenRecord> {
  return ApiHttp.fetch(`banking/tokens/${uuid}/`, { method: "GET" });
}

export function updateBankingToken(
  uuid: string,
  submitData: TokenSubmitData,
): Promise<BankingTokenRecord> {
  return ApiHttp.fetch(
    `banking/tokens/${uuid}/`,
    { method: "PUT" },
    { ...submitData },
  );
}

export async function destroyBankingToken(
  uuid: string,
): Promise<BankingTokenRecord> {
  await ApiHttp.fetch(`banking/tokens/${uuid}/`, { method: "DELETE" });
  return retrieveBankingToken(uuid);
}

export function getClientIp(): Promise<{ ip: string }> {
  return ApiHttp.fetch("whatismyip/");
}

export type ScopeGroup = {
  resource: string;
  label: string;
  read?: string;
  write?: string;
};

type BankingSchema = {
  components?: {
    securitySchemes?: {
      oauth2?: {
        flows?: { authorizationCode?: { scopes?: Record<string, string> } };
      };
    };
  };
};

const toLabel = (value: string) => {
  const words = value.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Builds scope groups from the banking OpenAPI schema's oauth2 scopes. */
export async function getScopeGroups(): Promise<ScopeGroup[]> {
  const schema: BankingSchema = await ApiHttp.fetch("banking/schema/?format=json", {
    method: "GET",
  });
  const scopes =
    schema?.components?.securitySchemes?.oauth2?.flows?.authorizationCode
      ?.scopes ?? {};
  const groups = new Map<string, ScopeGroup>();
  Object.keys(scopes).forEach((scope) => {
    const match = /^banking:(.+):(read|write)$/.exec(scope);
    if (!match) return;
    const [, resource, action] = match;
    const group = groups.get(resource) ?? {
      resource,
      label: toLabel(resource),
    };
    group[action as "read" | "write"] = scope;
    groups.set(resource, group);
  });
  // Scopes render in alphabetical order by resource name (e.g. "Accounts"
  // before "Transactions") so the list stays predictable as the schema grows.
  return Array.from(groups.values()).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}


function formatTimestamp(timestamp: string | null) {
  if (!timestamp) return "N/A";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function deserializeBankingToken(
  token: BankingTokenRecord | CreatedBankingTokenRecord,
): DeserializedBankingToken {
  const readCount = token.scope.filter((scope) =>
    scope.endsWith(":read"),
  ).length;
  const writeCount = token.scope.filter((scope) =>
    scope.endsWith(":write"),
  ).length;
  // Per design, the summary omits any zero side (e.g. "15 read", "5 write",
  // "7 read / 2 write") rather than always printing both counts.
  const scopesDisplay = [
    readCount > 0 ? `${readCount} read` : null,
    writeCount > 0 ? `${writeCount} write` : null,
  ]
    .filter(Boolean)
    .join(" / ");
  return {
    uuid: token.uuid,
    nickname: token.nickname,
    lastUsed: formatTimestamp(token.last_used),
    created: formatTimestamp(token.created),
    expires: formatTimestamp(token.expires),
    isExpired: new Date(token.expires) < new Date(),
    createdBy: token.username,
    scopes: new Set(token.scope),
    scopesDisplay,
    ipAllowlist: token.ip_allowlist,
    ipAllowListDisplay: `${token.ip_allowlist.length} IPs allowed`,
  };
}
